import { useState, useEffect } from 'react';
import { pbLog } from '../utils/log';
import {
  fetchCouriers,
  fetchCouriersByRegion,
  fetchCourier,
} from '../fetch/fetchCouriers';
import { ReqEnum } from 'const/requestStatus';
import {
  CourierJson,
  CourierRatingJson,
  CourierRetentionJson,
} from 'types/bundleTypes';
import { Region, reqStatus } from '../types/orderTypes';
import { fetchCouriersRating } from '../fetch/fetchCouriersRating';
import { fetchCouriersRetention } from '../fetch/fetchCouriersRetention';

const LOG_RESET_STATE = 'Reset state';
const LOG_FETCH_COURIERS = 'Fetching couriers';
const LOG_FETCH_COURIERS_SUCCESS = 'Fetched %d couriers';
const LOG_FETCH_COURIERS_RATING = 'Fetching couriers rating';

export const log = pbLog('containers:CouriersFetch');

export function useFetchCouriers(onlyUnLocked = false): any[] {
  const [fetchStatus, setFetchStatus] = useState<reqStatus>(ReqEnum.INIT);
  const [couriers, setCouriers] = useState<CourierJson[]>([]);

  useEffect(() => {
    log(LOG_RESET_STATE);
    setFetchStatus(ReqEnum.PENDING);
    log(LOG_FETCH_COURIERS);
    const fetchCouriersMethod = async () => {
      const couriers: CourierJson[] = (await fetchCouriers()).filter(courier =>
        onlyUnLocked ? !courier.isLocked : courier
      );
      log(LOG_FETCH_COURIERS_SUCCESS, couriers.length);

      setCouriers(couriers);
      setFetchStatus(ReqEnum.SUCCESS);
    };
    fetchCouriersMethod();
  }, [onlyUnLocked]);

  return [couriers, fetchStatus];
}

export function useFetchCouriersByRegion(
  onlyUnLocked = false,
  region: Region
): any[] {
  const [fetchStatus, setFetchStatus] = useState<reqStatus>(ReqEnum.INIT);
  const [couriers, setCouriers] = useState<CourierJson[]>([]);

  useEffect(() => {
    log(LOG_RESET_STATE);
    setFetchStatus(ReqEnum.PENDING);
    log(LOG_FETCH_COURIERS);
    const fetchCouriersMethod = async () => {
      const couriers: CourierJson[] = (
        await fetchCouriersByRegion(region)
      ).filter(courier => (onlyUnLocked ? !courier.isLocked : courier));
      log(LOG_FETCH_COURIERS_SUCCESS, couriers.length);

      setCouriers(couriers);
      setFetchStatus(ReqEnum.SUCCESS);
    };
    fetchCouriersMethod();
  }, [onlyUnLocked, region]);

  return [couriers, fetchStatus];
}

export function useFetchCourierById(courierId: string | undefined): any[] {
  const [fetchStatus, setFetchStatus] = useState<reqStatus>(ReqEnum.INIT);
  const [courier, setCourier] = useState<CourierJson>();

  useEffect(() => {
    log(LOG_RESET_STATE);
    setFetchStatus(ReqEnum.PENDING);
    log(LOG_FETCH_COURIERS);

    if (courierId === undefined || Number(courierId) === 0) {
      setCourier(null);
      setFetchStatus(ReqEnum.ERROR);
    } else {
      const fetchCouriersMethod = async (courierId: string) => {
        const courier: CourierJson = await fetchCourier(courierId);
        log('Fetched courier', courier.fullName);

        setCourier(courier);
        setFetchStatus(ReqEnum.SUCCESS);
      };
      fetchCouriersMethod(courierId);
    }
  }, [courierId]);

  return [courier, fetchStatus];
}

export function useFetchCouriersRating(
  start: string,
  end: string,
  courierIds: string[]
): any[] {
  const [fetchStatus, setFetchStatus] = useState<reqStatus>(ReqEnum.INIT);
  const [couriersRating, setCouriersRating] = useState<CourierRatingJson[]>([]);

  const courierIdsAsString = courierIds.join(',');

  useEffect(() => {
    log(LOG_RESET_STATE);
    setFetchStatus(ReqEnum.PENDING);
    log(LOG_FETCH_COURIERS_RATING);

    const courierIdsToUse =
      courierIdsAsString.trim() === '' ? [] : courierIdsAsString.split(',');

    const fetchCouriersRatingMethod = async () => {
      const couriersRating: CourierRatingJson[] = await fetchCouriersRating(
        start,
        end,
        courierIdsToUse
      );

      log(LOG_FETCH_COURIERS_SUCCESS, couriersRating.length);
      setCouriersRating(couriersRating);
      setFetchStatus(ReqEnum.SUCCESS);
    };
    fetchCouriersRatingMethod();
  }, [courierIdsAsString, end, start]);

  return [couriersRating, fetchStatus];
}

export function useFetchCouriersRetentionForWorkLately(
  start: string,
  end: string,
  courierIds: string[]
): any[] {
  const [fetchStatus, setFetchStatus] = useState<reqStatus>(ReqEnum.INIT);
  const [couriersRetentionForWorkLately, setCouriersRetentionForWorkLately] =
    useState<CourierRetentionJson[]>([]);

  const courierIdsAsString = courierIds.join(',');

  useEffect(() => {
    log(LOG_RESET_STATE);
    setFetchStatus(ReqEnum.PENDING);
    log(LOG_FETCH_COURIERS_RATING);

    const courierIdsToUse =
      courierIdsAsString.trim() === '' ? [] : courierIdsAsString.split(',');

    const fetchCouriersRetentionMethod = async () => {
      const couriersRetentionForWorkLately: CourierRetentionJson[] =
        await fetchCouriersRetention(start, end, courierIdsToUse);
      if (couriersRetentionForWorkLately) {
        log(LOG_FETCH_COURIERS_SUCCESS, couriersRetentionForWorkLately.length);
      }

      setCouriersRetentionForWorkLately(couriersRetentionForWorkLately);
      setFetchStatus(ReqEnum.SUCCESS);
    };
    fetchCouriersRetentionMethod();
  }, [courierIdsAsString, end, start]);

  return [couriersRetentionForWorkLately, fetchStatus];
}
